import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Restaurant, RestaurantImage } from "@/lib/types";
import { queryClient, safeParseResponse } from "@/lib/queryClient";
import { queryKeys, invalidateRestaurantDetail, invalidateAllRestaurantLists } from "@/lib/queryKeys";
import { useToast } from "@/hooks/use-toast";
import { 
  Loader2, Upload, FileText, FilePlus2, 
  File, XCircle, FileCheck, AlertCircle, Trash, 
  Eye, Download, FileImage 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface RestaurantFormMenusProps {
  restaurant: Restaurant;
  onDirtyChange: (isDirty: boolean) => void;
  onSavingChange: (isSaving: boolean) => void;
}

const RestaurantFormMenus = ({ restaurant, onDirtyChange, onSavingChange }: RestaurantFormMenusProps) => {
  const { toast } = useToast();
  const isDev = import.meta.env.DEV;
  const [error, setError] = useState<string | null>(null);
  const [uploadedMenus, setUploadedMenus] = useState<File[]>([]);
  const [menuPreviews, setMenuPreviews] = useState<{
    file: File;
    name: string;
    type: string;
    preview: string | null;
    size: string;
  }[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeletingMenu, setIsDeletingMenu] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("upload");
  const menuFileInputRef = useRef<HTMLInputElement>(null);
  // const [imageLoadError, setImageLoadError] = useState<string[]>([]);

  // Abfrage der vorhandenen Speisekarten - Nutze zentrale queryKeys
  const { 
    data: menus = [], 
    isLoading,
    refetch: _refetch 
  } = useQuery<RestaurantImage[]>({
    queryKey: queryKeys.restaurants.menus(restaurant.id),
    enabled: Boolean(restaurant.id && restaurant.id !== 'new'),
    staleTime: 30 * 1000, // 30s: reduziert unnötige Requests bei Tab-Wechsel
    gcTime: 5 * 60 * 1000, // 5 Minuten im Cache behalten
    retry: 3,
    retryDelay: 1000,
    queryFn: async () => {
      const response = await fetch(`/api/restaurants/${restaurant.id}/menus`, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`Fehler beim Laden der Speisekarten: ${response.status}`);
      }
      
      const data = await safeParseResponse(response);
      if (!data) {
        throw new Error("Leere Antwort vom Server");
      }
      if (isDev) console.log("Speisekarten geladen:", data);
      return data;
    }
  });

  // Effekt für ungespeicherte Änderungen
  useEffect(() => {
    onDirtyChange(uploadedMenus.length > 0);
  }, [uploadedMenus, onDirtyChange]);

  // Formatiert die Dateigröße
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  // Speisekarten-Upload Mutation
  const uploadMenusMutation = useMutation({
    mutationFn: async ({ restaurantId, menuFiles }: { restaurantId: string, menuFiles: File[] }) => {
      const formData = new FormData();
      
      menuFiles.forEach(file => {
        formData.append('menus', file);
      });
      
      const response = await fetch(`/api/restaurants/${restaurantId}/upload-menus`, {
        method: 'POST',
        body: formData,
        headers: {
          // Keine Cache-Header für multipart/form-data
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        // Verbesserte Fehlerbehandlung
        let errorMessage = `HTTP-Fehler! Status: ${response.status}`;
        
        try {
          // P0: Sichere Error-Parsing
          const errorData = await safeParseResponse<{ error?: string; message?: string }>(response);
          errorMessage = errorData?.error || errorData?.message || errorMessage;
        } catch (parseError) {
          console.error("Fehler beim Parsen der Fehlerantwort:", parseError);
        }
        
        throw new Error(errorMessage);
      }
      
      // P0: Sichere Response-Parsing
      const result = await safeParseResponse(response);
      if (result) {
        return result;
      }
      if (response.status === 204) {
        return { success: true };
      }
      const text = await response.text();
      if (text && text.trim().startsWith('{')) {
        try { return JSON.parse(text); } catch {}
      }
      return { success: true };
    },
    onSuccess: async (_data) => {
      // Speisekarten zurücksetzen
      setUploadedMenus([]);
      setMenuPreviews([]);
      
      toast({
        title: "Speisekarten hochgeladen",
        description: `${uploadedMenus.length} Speisekarten wurden erfolgreich hochgeladen.`
      });
      
      // Explizites Deaktivieren des Speicherstatus
      onSavingChange(false);
      onDirtyChange(false);
      
      // ✅ KORREKTE INVALIDIERUNG mit zentralen queryKeys
      await queryClient.invalidateQueries({
        queryKey: queryKeys.restaurants.menus(restaurant.id),
        refetchType: 'all'
      });
      
      // Restaurant Detail invalidieren (für aktualisierte Menü-Anzahl)
      invalidateRestaurantDetail(queryClient, restaurant.id, { includeSubResources: false });
      
      // Slug-basierte Queries invalidieren
      if (restaurant.slug) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.restaurants.bySlug(restaurant.slug),
          refetchType: 'all'
        });
      }
      
      // Listen invalidieren
      invalidateAllRestaurantLists(queryClient);
      
      // Tab nach dem Upload wechseln
      setActiveTab("existing");
      
      if (isDev) console.log("Speisekarten wurden erfolgreich hochgeladen");
    },
    onError: (error: Error) => {
      setError(`Fehler beim Hochladen: ${error.message}`);
      toast({
        title: "Fehler beim Hochladen",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Speisekarten-Lösch Mutation
  const deleteMenuMutation = useMutation({
    mutationFn: async ({ restaurantId, menuId }: { restaurantId: string, menuId: string | number }) => {
      const response = await fetch(`/api/restaurants/${restaurantId}/menus/${menuId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache', 
          'Expires': '0'
        }
      });
      
      if (!response.ok) {
        // Verbesserte Fehlerbehandlung
        let errorMessage = `HTTP-Fehler! Status: ${response.status}`;
        
        try {
          // Versuche die Antwort als JSON zu parsen
          const contentType = response.headers.get('Content-Type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          }
        } catch (parseError) {
          console.error("Fehler beim Parsen der Fehlerantwort:", parseError);
        }
        
        throw new Error(errorMessage);
      }
      
      return { success: true };
    },
    onSuccess: async () => {
      toast({
        title: "Speisekarte gelöscht",
        description: "Die Speisekarte wurde erfolgreich gelöscht."
      });
      
      // ✅ KORREKTE INVALIDIERUNG mit zentralen queryKeys
      await queryClient.invalidateQueries({
        queryKey: queryKeys.restaurants.menus(restaurant.id),
        refetchType: 'all'
      });
      
      // Restaurant Detail invalidieren
      invalidateRestaurantDetail(queryClient, restaurant.id, { includeSubResources: false });
      
      // Slug-basierte Queries invalidieren
      if (restaurant.slug) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.restaurants.bySlug(restaurant.slug),
          refetchType: 'all'
        });
      }
      
      // Listen invalidieren
      invalidateAllRestaurantLists(queryClient);
      
      if (isDev) console.log("Speisekarte erfolgreich gelöscht");
      
      setIsDeletingMenu(null);
    },
    onError: (error: Error) => {
      setError(`Fehler beim Löschen: ${error.message}`);
      toast({
        title: "Fehler beim Löschen",
        description: error.message,
        variant: "destructive"
      });
      setIsDeletingMenu(null);
    }
  });

  // Handler für Speisekarten-Upload
  const handleMenuUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      
      // Nur PDF und Bilder erlauben
      const allowedFiles = fileArray.filter(file => 
        file.type.startsWith('image/') || 
        file.type === 'application/pdf'
      );
      
      if (allowedFiles.length !== fileArray.length) {
        toast({
          title: "Ungültige Dateien",
          description: "Es werden nur PDF-Dokumente und Bilder akzeptiert.",
          variant: "destructive"
        });
      }
      
      setUploadedMenus(prev => [...prev, ...allowedFiles]);
      
      // Erstelle Vorschaudaten
      const newPreviews = allowedFiles.map(file => {
        const isImage = file.type.startsWith('image/');
        const isPdf = file.type === 'application/pdf';
        
        return {
          file,
          name: file.name,
          type: file.type,
          preview: isImage ? URL.createObjectURL(file) : (isPdf ? 'pdf' : null),
          size: formatFileSize(file.size)
        };
      });
      
      setMenuPreviews(prev => [...prev, ...newPreviews]);
    }
  };

  // Entferne eine hochgeladene Speisekarte vor dem tatsächlichen Upload
  const removeUploadedMenu = (index: number) => {
    // URL.revokeObjectURL freigeben, um Speicherlecks zu vermeiden
    const preview = menuPreviews[index];
    if (preview.preview && preview.preview !== 'pdf') {
      URL.revokeObjectURL(preview.preview);
    }
    
    setUploadedMenus(prev => {
      const newUploads = [...prev];
      newUploads.splice(index, 1);
      return newUploads;
    });
    
    setMenuPreviews(prev => {
      const newPreviews = [...prev];
      newPreviews.splice(index, 1);
      return newPreviews;
    });
  };

  // Lösche eine bereits hochgeladene Speisekarte
  const deleteMenu = (menuId: string | number) => {
    if (window.confirm("Möchten Sie diese Speisekarte wirklich löschen?")) {
      setIsDeletingMenu(String(menuId));
      deleteMenuMutation.mutate({ restaurantId: restaurant.id, menuId });
    }
  };

  // Upload-Funktion
  const uploadMenuFiles = async () => {
    if (uploadedMenus.length === 0) {
      toast({
        title: "Keine Speisekarten",
        description: "Bitte wählen Sie mindestens eine Speisekarte zum Hochladen aus.",
        variant: "destructive"
      });
      return;
    }
    
    setIsUploading(true);
    onSavingChange(true);
    setError(null);
    
    try {
      if (isDev) console.log(`Starte Upload von ${uploadedMenus.length} Speisekarten...`);
      await uploadMenusMutation.mutateAsync({
        restaurantId: restaurant.id,
        menuFiles: uploadedMenus
      });
      if (isDev) console.log("Upload abgeschlossen");
    } catch (err) {
      console.error("Fehler beim Upload:", err);
      if (err instanceof Error) {
        setError(`Fehler beim Upload: ${err.message}`);
      }
    } finally {
      setIsUploading(false);
      onSavingChange(false);
    }
  };

  // Datei-Typ Icon bestimmen
  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return <FileImage className="h-8 w-8 text-blue-500" />;
    } else if (fileType === 'application/pdf') {
      return <FileText className="h-8 w-8 text-red-500" />;
    } else {
      return <File className="h-8 w-8 text-gray-500" />;
    }
  };

  // Zählen der verschiedenen Dateitypen
  const getFileTypeCounts = () => {
    const images = menus.filter(menu => menu.path.match(/\.(jpg|jpeg|png|gif|webp)$/i)).length;
    const pdfs = menus.filter(menu => menu.path.endsWith('.pdf')).length;
    const others = menus.length - images - pdfs;
    
    return { images, pdfs, others };
  };

  // Funktion zum Korrigieren von Bildpfaden
  const getFixedImagePath = (originalPath: string): string => {
    if (!originalPath) return '';
    
    console.log(`Korrigiere Bildpfad von ${originalPath}`);
    
    // Wenn der Pfad bereits mit dem Server-Präfix beginnt, diesen beibehalten
    if (originalPath.startsWith('/')) {
      return originalPath;
    }
    
    // Ansonsten Pfad mit '/' prefixen
    return `/${originalPath}`;
  };

  // Komponente für robuste Bildanzeige mit Fallback-Optionen
  const RobustMenuImage = ({ src, alt, className }: { src: string; alt?: string; className?: string }) => {
    const [currentSrc, setCurrentSrc] = useState(src);
    const [errorCount, setErrorCount] = useState(0);
    
    const handleError = () => {
      console.log(`Fehler beim Laden des Bildes: ${currentSrc}`);
      
      if (errorCount === 0) {
        // Erster Versuch: Wenn Pfad mit /uploads beginnt, versuche /public/uploads
        if (currentSrc.startsWith('/uploads/')) {
          const newSrc = `/public${currentSrc}`;
          console.log(`Versuche alternativen Pfad: ${newSrc}`);
          setCurrentSrc(newSrc);
          setErrorCount(1);
          return;
        }
        // Wenn es bereits mit /public/ beginnt, überspringe direkt zum Fallback
        setErrorCount(2);
      } 
      else if (errorCount === 1) {
        setErrorCount(2);
      }
      
      // Fallback zu Platzhalter (gehe direkt zum Root-Platzhalter, nicht zu /public/placeholder)
      if (errorCount >= 1) {
        console.log(`Verwende Fallback-Bild`);
        // Verwende absoluten Pfad mit Timestamp, um Cache-Probleme zu vermeiden
        setCurrentSrc(`/placeholder-menu.jpg?t=${Date.now()}`);
      }
    };
    
    return (
      <img 
        src={currentSrc} 
        alt={alt || "Speisekarte"} 
        className={className || "w-full h-auto object-contain rounded-lg"} 
        onError={handleError}
      />
    );
  };

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Speisekarten verwalten</CardTitle>
          <CardDescription>
            Laden Sie Speisekarten als PDF oder Bilder hoch und verwalten Sie bestehende Dokumente
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {/* Mobile: Horizontal scrollable Tabs mit kürzeren Labels */}
            <TabsList
              className="mb-6 md:grid md:grid-cols-2 flex flex-nowrap md:flex-none overflow-x-auto gap-2 p-1 bg-gray-50 rounded-lg scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <TabsTrigger value="upload" className="shrink-0 min-w-[120px] md:min-w-0 whitespace-nowrap">
                <FilePlus2 className="h-4 w-4 mr-1 md:mr-2" />
                <span className="hidden md:inline">Speisekarten hochladen</span>
                <span className="md:hidden">Hochladen</span>
              </TabsTrigger>
              <TabsTrigger value="existing" className="shrink-0 min-w-[120px] md:min-w-0 whitespace-nowrap">
                <FileText className="h-4 w-4 mr-1 md:mr-2" />
                <span className="hidden md:inline">Vorhandene Speisekarten</span>
                <span className="md:hidden">Vorhandene</span>
                {menus.length > 0 && (
                  <Badge className="ml-1 md:ml-2" variant="secondary">{menus.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upload" className="mt-0">
              <div className="space-y-6">
                {/* Drag & Drop Upload Zone */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    ref={menuFileInputRef}
                    type="file"
                    id="menu-upload"
                    accept="image/*,application/pdf"
                    multiple
                    onChange={handleMenuUpload}
                    className="hidden"
                  />
                  
                  <Label htmlFor="menu-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <Upload className="h-10 w-10 text-gray-400" />
                      <h3 className="text-lg font-medium">Speisekarten hochladen</h3>
                      <p className="text-sm text-gray-500">
                        Klicken Sie hier, um Speisekarten auszuwählen oder ziehen Sie Dateien in diesen Bereich
                      </p>
                      <p className="text-xs text-gray-400">
                        Erlaubte Formate: PDF, JPG, PNG, GIF
                      </p>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (menuFileInputRef.current) {
                            menuFileInputRef.current.click();
                          }
                        }}
                      >
                        Dateien auswählen
                      </Button>
                    </div>
                  </Label>
                </div>

                {/* Vorschau der hochzuladenden Speisekarten */}
                {uploadedMenus.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium">Ausgewählte Speisekarten ({uploadedMenus.length})</h3>
                    <ScrollArea className="h-[350px] rounded-md border p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {menuPreviews.map((item, index) => (
                          <div key={index} className="flex flex-col border rounded-lg overflow-hidden">
                            <div className="aspect-[4/3] bg-gray-50 flex items-center justify-center relative group">
                              {item.preview && item.preview !== 'pdf' ? (
                                <RobustMenuImage
                                  src={item.preview}
                                  alt={item.name}
                                  className="w-full h-full object-contain"
                                />
                              ) : (
                                getFileIcon(item.type)
                              )}
                              
                              <button
                                type="button"
                                onClick={() => removeUploadedMenu(index)}
                                className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <XCircle className="h-5 w-5 text-red-600" />
                              </button>
                            </div>
                            <div className="p-3 bg-white">
                              <p className="text-sm font-medium truncate" title={item.name}>
                                {item.name}
                              </p>
                              <div className="flex justify-between items-center mt-1">
                                <span className="text-xs text-gray-500">{item.size}</span>
                                <Badge variant="outline" className="text-xs">
                                  {item.type.split('/')[1].toUpperCase()}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    <div className="flex justify-end">
                      <Button 
                        type="button" 
                        onClick={uploadMenuFiles}
                        disabled={isUploading}
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Wird hochgeladen...
                          </>
                        ) : (
                          <>
                            <FileCheck className="mr-2 h-4 w-4" />
                            {uploadedMenus.length} Speisekarten hochladen
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="existing" className="mt-0">
              <div className="space-y-4">
                {isLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : menus.length === 0 ? (
                  <div className="text-center py-12 border rounded-lg bg-gray-50">
                    <FileText className="h-12 w-12 mx-auto text-gray-400" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">Keine Speisekarten</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Es wurden noch keine Speisekarten für dieses Restaurant hochgeladen.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-4"
                      onClick={() => setActiveTab("upload")}
                    >
                      <FilePlus2 className="h-4 w-4 mr-2" />
                      Speisekarten hochladen
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(() => {
                        const counts = getFileTypeCounts();
                        return (
                          <>
                            {counts.images > 0 && (
                              <Badge variant="secondary" className="flex items-center gap-1">
                                <FileImage className="h-3 w-3" />
                                {counts.images} Bilder
                              </Badge>
                            )}
                            {counts.pdfs > 0 && (
                              <Badge variant="secondary" className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {counts.pdfs} PDFs
                              </Badge>
                            )}
                            {counts.others > 0 && (
                              <Badge variant="secondary" className="flex items-center gap-1">
                                <File className="h-3 w-3" />
                                {counts.others} Andere
                              </Badge>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    
                    <ScrollArea className="h-[450px] rounded-md border">
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 p-4">
                        {menus.map((menu, index) => {
                          const isImage = menu.path.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                          const isPdf = menu.path.endsWith('.pdf');
                          
                          return (
                            <div key={menu.id} className="flex flex-col border rounded-lg overflow-hidden">
                              <div className="aspect-[4/3] bg-gray-50 flex items-center justify-center relative group">
                                {/* Klickbarer Container, der den Dialog öffnet */}
                                <div 
                                  className="w-full h-full flex items-center justify-center cursor-pointer"
                                  onClick={() => setPreviewFile(menu.path)}
                                >
                                  {isImage ? (
                                    <RobustMenuImage
                                      src={getFixedImagePath(menu.path)}
                                      alt={`Speisekarte ${index + 1}`}
                                      className="rounded-lg shadow-md max-h-[700px] w-auto mx-auto"
                                    />
                                  ) : (
                                    <FileText className="h-16 w-16 text-red-500" />
                                  )}
                                </div>
                                
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-colors duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                                  <div className="flex space-x-2">
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            type="button"
                                            variant="secondary"
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation(); // Verhindert Bubble-Up zum übergeordneten Click-Handler
                                              setPreviewFile(menu.path);
                                            }}
                                          >
                                            <Eye className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>Anzeigen</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                    
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            type="button"
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => {
                                              const a = document.createElement('a');
                                              a.href = menu.path;
                                              a.download = menu.path.split('/').pop() || 'speisekarte';
                                              document.body.appendChild(a);
                                              a.click();
                                              document.body.removeChild(a);
                                            }}
                                          >
                                            <Download className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>Herunterladen</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                    
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            type="button"
                                            variant="destructive"
                                            size="sm"
                                            onClick={() => deleteMenu(menu.id)}
                                            disabled={isDeletingMenu === String(menu.id)}
                                          >
                                            {isDeletingMenu === String(menu.id) ? (
                                              <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                              <Trash className="h-4 w-4" />
                                            )}
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>Löschen</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="p-3 bg-white">
                                <p className="text-sm font-medium truncate" title={menu.path.split('/').pop()}>
                                  {menu.path.split('/').pop()}
                                </p>
                                <div className="flex justify-between items-center mt-1">
                                  <span className="text-xs text-gray-500">
                                    {menu.caption || "Speisekarte"}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {isPdf ? 'PDF' : (isImage ? 'Bild' : 'Datei')}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Vorschau Dialog für Bilder und PDFs */}
      <Dialog open={!!previewFile} onOpenChange={(open) => !open && setPreviewFile(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Vorschau</DialogTitle>
            <DialogDescription>
              {previewFile && previewFile.split('/').pop()}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 max-h-[70vh] overflow-auto">
            {previewFile && previewFile.endsWith('.pdf') ? (
              <iframe 
                src={previewFile} 
                className="w-full h-[70vh]"
                title="PDF Vorschau"
              />
            ) : previewFile && (
              <RobustMenuImage
                src={previewFile}
                alt="Speisekarte"
                className="max-w-full h-auto mx-auto"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RestaurantFormMenus; 